/* global window setInterval clearInterval */
import React from "react";
import PropTypes from "prop-types";
import Relay from "react-relay";
import PagingGrid, { defaultPageSize, processPagingParams } from "../fw/PagingGrid.react.js";
import AppPanel from "../fw/AppPanel.react.js";
import { injectIntl, defineMessages, FormattedMessage } from "react-intl";
import { Link } from "react-router";
import { StorageAdapter } from "../utils/StorageAdapter.ts";

import types from "../../common/model-api/types.js";
import urlMapping from "../app/URLMapping.js";
import { wrapNotFound } from "../app/NotFoundHandler.react.js";

import RetryIncomingEmailsMutation from "../flow-defs/RetryIncomingEmailsMutation.react";
import CancelJobMutation from "../jobs/CancelJobMutation.react";
import { showJobNotification } from "../app/notification";
import ConfirmDialog from "../fw/ConfirmDialog.react";
import LoadingButton from "../fw/LoadingButton.react";
import * as BrowserPermission from "../BrowserPermission";
import { safeJSONparse } from "../../common/util/safeJSONparse";

// Preload following screens
import(/* webpackChunkName: "FlowIncomingEmailView" */ "./FlowIncomingEmailView.react.js");

const messages = defineMessages({
	title: { id: "flowIncomingEmailList.title", defaultMessage: "List of Incoming emails" },
	retryFailedIncomingEmailsButton: { id: "FlowIncomingEmailList.retryFailedIncomingEmailsButton", defaultMessage: "Retry failed recevied e-mails" },
	retryFailedIncomingEmailsDescription: { id: "FlowIncomingEmailList.retryFailedIncomingEmailsDescription", defaultMessage: "Do you really want to retry failed received e-mails?" },
	retryUnprocessedIncomingEmailsButton: { id: "FlowIncomingEmailList.retryUnprocessedIncomingEmailsButton", defaultMessage: "Retry unprocessed recevied e-mails" },
	retryUnprocessedIncomingEmailsDescription: { id: "FlowIncomingEmailList.retryUnprocessedIncomingEmailsDescription", defaultMessage: "Do you really want to retry unprocessed received e-mails?" },
	processBatchButton: { id: "FlowIncomingEmailList.processBatchButton", defaultMessage: "Process e-mails in date range" },
	processBatchDescription: { id: "FlowIncomingEmailList.processBatchDescription", defaultMessage: "Do you really want to process selected e-mails?" },
	stopOnFailureLabel: { id: "FlowIncomingEmailList.stopOnFailureLabel", defaultMessage: "Stop on invocation failure" },
	continueOnFailureLabel: { id: "FlowIncomingEmailList.continueOnFailureLabel", defaultMessage: "Continue on invocation failure" },
	fromDateLabel: { id: "FlowIncomingEmailList.fromDateLabel", defaultMessage: "From" },
	toDateLabel: { id: "FlowIncomingEmailList.toDateLabel", defaultMessage: "To" },
	toggleBatchSectionButton: { id: "FlowIncomingEmailList.toggleBatchSectionButton", defaultMessage: "Process all e-mails in date range" },
	batchFailedLabel: { id: "flowIncomingEmailList.listFailed", defaultMessage: "Failed" },
	batchUnprocessedLabel: { id: "flowIncomingEmailList.listUnprocessed", defaultMessage: "Unprocessed" },
	batchHintTitle: { id: "FlowIncomingEmailList.batchHintTitle", defaultMessage: "How batch processing works" },
	batchHintSequential: { id: "FlowIncomingEmailList.batchHintSequential", defaultMessage: "E-mails are processed sequentially: the next e-mail will not start until the previous invocation is finished." },
	batchHintContinue: { id: "FlowIncomingEmailList.batchHintContinue", defaultMessage: "Continue on failure: if an invocation fails, the job continues with the next e-mail." },
	batchHintStop: { id: "FlowIncomingEmailList.batchHintStop", defaultMessage: "Stop on failure: the job stops immediately on the first invocation error." },
	stopBatchButton: { id: "FlowIncomingEmailList.stopBatchButton", defaultMessage: "Stop batch job" },
	stopBatchDescription: { id: "FlowIncomingEmailList.stopBatchDescription", defaultMessage: "Do you really want to stop current batch job?" },
	batchRunningLabel: { id: "FlowIncomingEmailList.batchRunningLabel", defaultMessage: "Batch job is running" },
	batchProgressLabel: { id: "FlowIncomingEmailList.batchProgressLabel", defaultMessage: "Progress: {current}/{max}" },
	batchTotalLabel: { id: "FlowIncomingEmailList.batchTotalLabel", defaultMessage: "Total to retry: {count}" },
	batchProcessedLabel: { id: "FlowIncomingEmailList.batchProcessedLabel", defaultMessage: "Processed: {count}" },
	batchRemainingLabel: { id: "FlowIncomingEmailList.batchRemainingLabel", defaultMessage: "Remaining: {count}" },
	batchCurrentTaskLabel: { id: "FlowIncomingEmailList.batchCurrentTaskLabel", defaultMessage: "Current phase: {phase}" },
	batchUnknownPhaseLabel: { id: "FlowIncomingEmailList.batchUnknownPhaseLabel", defaultMessage: "preparing" },
	batchPhaseWaitingLabel: { id: "FlowIncomingEmailList.batchPhaseWaitingLabel", defaultMessage: "waiting" },
	batchPhaseProcessingLabel: { id: "FlowIncomingEmailList.batchPhaseProcessingLabel", defaultMessage: "processing" },
	batchPhaseDoneLabel: { id: "FlowIncomingEmailList.batchPhaseDoneLabel", defaultMessage: "done" },
	batchProgressLoadingLabel: { id: "FlowIncomingEmailList.batchProgressLoadingLabel", defaultMessage: "Loading current batch progress..." },
	batchValidationDateRequired: { id: "FlowIncomingEmailList.batchValidationDateRequired", defaultMessage: "Please fill both dates: From and To." },
	batchValidationDateOrder: { id: "FlowIncomingEmailList.batchValidationDateOrder", defaultMessage: "Date From must be earlier than or equal to Date To." },
});

class FlowIncomingEmailList extends React.Component {
	_getJobRefId = (job) => {
		if (!job) {
			return null;
		}

		return job.id || job.realId || null;
	};

	_isSameJobRef = (job, refId) => {
		if (!job || !refId) {
			return false;
		}

		return job.id === refId || job.realId === refId;
	};

	_isRunningStatus = (status) => {
		return status === "waiting" || status === "in-progress" || status === "running";
	};

	_jobFlowMatches = (job, flowRealId, flowId) => {
		if (!job) {
			return false;
		}

		const args = this._parseJobArgs(job.args);
		if (!args || !args.flow_id) {
			return false;
		}

		return args.flow_id === flowRealId || args.flow_id === flowId;
	};

	_findRunningBatchJobFromProps = () => {
		const organisation = ((this.props || {}).user || {}).organisation || {};
		const flow = organisation.flow || {};
		const flowRealId = flow.realId;
		const flowId = flow.id;
		const jobs = this._getAllKnownJobsFromProps();

		return (
			jobs.find((item) => {
				return item && item.type === "retry-incoming-mails" && this._isRunningStatus(item.status) && this._jobFlowMatches(item, flowRealId, flowId);
			}) || null
		);
	};

	_getAllKnownJobsFromProps = () => {
		const user = (this.props || {}).user || {};
		const organisation = user.organisation || {};
		const organisationJobs = ((organisation.jobs && organisation.jobs.edges) || []).map((edge) => edge.node).filter(Boolean);
		const userJobs = ((user.jobs && user.jobs.edges) || []).map((edge) => edge.node).filter(Boolean);
		const mergedById = {};

		organisationJobs.concat(userJobs).forEach((job) => {
			const key = job.id || job.realId;
			if (key && !mergedById[key]) {
				mergedById[key] = job;
			}
		});

		return Object.keys(mergedById).map((key) => mergedById[key]);
	};

	_findBatchJobByRefFromProps = (refId) => {
		if (!refId) {
			return null;
		}

		const organisation = ((this.props || {}).user || {}).organisation || {};
		const selectedBatchJob = organisation.job;
		if (this._isSameJobRef(selectedBatchJob, refId)) {
			return selectedBatchJob;
		}

		const jobs = (organisation.jobs && organisation.jobs.edges) || [];
		return jobs.map((edge) => edge.node).find((job) => this._isSameJobRef(job, refId)) || null;
	};

	_syncStoredBatchJobFromProps = () => {
		const runningBatchJob = this._findRunningBatchJobFromProps();
		if (runningBatchJob) {
			this._setStoredBatchJobId(this._getJobRefId(runningBatchJob));
			return;
		}

		const organisation = ((this.props || {}).user || {}).organisation || {};
		const selectedBatchJob = organisation.job;
		const jobs = this._getAllKnownJobsFromProps();
		const storedBatchJobId = this._getStoredBatchJobId();
		const matchedStoredJob = storedBatchJobId ? jobs.find((job) => this._isSameJobRef(job, storedBatchJobId)) : null;

		if (selectedBatchJob && selectedBatchJob.type === "retry-incoming-mails" && this._isRunningStatus(selectedBatchJob.status)) {
			this._setStoredBatchJobId(this._getJobRefId(selectedBatchJob));
			return;
		}

		if (matchedStoredJob && matchedStoredJob.type === "retry-incoming-mails" && !this._isRunningStatus(matchedStoredJob.status)) {
			this._setStoredBatchJobId("");
			return;
		}

		if (selectedBatchJob && selectedBatchJob.type === "retry-incoming-mails" && !this._isRunningStatus(selectedBatchJob.status)) {
			this._setStoredBatchJobId("");
			return;
		}

		// Do not clear stored id when job is not found in current jobs window.
		// The jobs connection is paged and can temporarily miss the target job.
	};

	_getBatchValidationError = () => {
		const since = this.state.batchSinceDate;
		const to = this.state.batchToDate;

		if (!since || !to) {
			return messages.batchValidationDateRequired;
		}

		if (new Date(since).getTime() > new Date(to).getTime()) {
			return messages.batchValidationDateOrder;
		}

		return null;
	};

	_batchJobStorageKey = () => {
		return "flowIncomingEmails:batchJobByFlow";
	};

	_getBatchJobMap = () => {
		const mapRaw = StorageAdapter.getSync(this._batchJobStorageKey()) || "{}";
		const mapParsed = safeJSONparse(mapRaw);
		if (mapParsed && typeof mapParsed === "object") {
			return mapParsed;
		}

		return {};
	};

	_setBatchJobMap = (map) => {
		StorageAdapter.setSync(this._batchJobStorageKey(), JSON.stringify(map || {}));
	};

	_getFlowStorageKeys = () => {
		const organisation = (this.props.user || {}).organisation || {};
		const flow = organisation.flow || {};
		return [flow.realId, flow.id].filter(Boolean);
	};

	_getStoredBatchJobId = () => {
		const map = this._getBatchJobMap();
		const keys = this._getFlowStorageKeys();

		for (let i = 0; i < keys.length; i += 1) {
			const key = keys[i];
			if (map[key]) {
				return map[key];
			}
		}

		return "";
	};

	_setStoredBatchJobId = (jobId) => {
		const map = this._getBatchJobMap();
		const keys = this._getFlowStorageKeys();

		keys.forEach((key) => {
			if (jobId) {
				map[key] = jobId;
			} else {
				delete map[key];
			}
		});

		this._setBatchJobMap(map);
	};

	_parseJobArgs = (args) => {
		if (!args) {
			return null;
		}

		if (typeof args === "string") {
			return safeJSONparse(args);
		}

		return args;
	};

	_parseProgress = (progress) => {
		if (!progress) {
			return null;
		}

		if (typeof progress === "string") {
			return safeJSONparse(progress);
		}

		return progress;
	};

	typeName = "flowIncomingEmails";

	static propTypes = {
		intl: PropTypes.object.isRequired,
		relay: PropTypes.object,
		user: PropTypes.object.isRequired,
	};

	constructor() {
		super();

		this.state = {
			showBatchSection: false,
			batchSinceDate: "",
			batchToDate: "",
			batchFailed: false,
			batchUnprocessed: false,
			batchStopOnFailure: false,
			lastBatchJobId: null,
		};

		this.batchStatusPoller = null;
	}

	_refreshBatchStatus = () => {
		if (!this.props.relay) {
			return;
		}

		const batchJobId = this.state.lastBatchJobId || this._getStoredBatchJobId() || "";

		if (this.props.relay.forceFetch) {
			this.props.relay.forceFetch({ batchJobId });
		} else if (this.props.relay.setVariables) {
			this.props.relay.setVariables({ batchJobId });
		}
	};

	componentDidMount() {
		this._syncStoredBatchJobFromProps();
		this.batchStatusPoller = setInterval(() => {
			this._refreshBatchStatus();
		}, 5000);
	}

	componentDidUpdate() {
		this._syncStoredBatchJobFromProps();
	}

	componentWillUnmount() {
		if (this.batchStatusPoller) {
			clearInterval(this.batchStatusPoller);
			this.batchStatusPoller = null;
		}
	}

	onClickRetryFailedIncomingEmails() {
		return new Promise((resolve) => {
			let confirmDialogConfig = {
				message: this.props.intl.formatMessage(messages.retryFailedIncomingEmailsDescription),
				confirm: this.props.intl.formatMessage(messages.retryFailedIncomingEmailsButton),
				confirmStyle: "btn-danger",
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig)
				.then(() => {
					return this.retryFailedIncomingEmails().then(() => {
						return resolve();
					});
				})
				.catch(() => {
					resolve();
				});
		});
	}

	onClickProcessBatchIncomingEmails() {
		const validationMessage = this._getBatchValidationError();
		if (validationMessage) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			let confirmDialogConfig = {
				message: this.props.intl.formatMessage(messages.processBatchDescription),
				confirm: this.props.intl.formatMessage(messages.processBatchButton),
				confirmStyle: "btn-danger",
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig)
				.then(() => {
					return this.processBatchIncomingEmails().then(() => {
						return resolve();
					});
				})
				.catch(() => {
					resolve();
				});
		});
	}

	processBatchIncomingEmails() {
		let { user } = this.props;

		return new Promise((resolve, reject) => {
			let onSuccess = (response) => {
				if (response.retryIncomingEmails.job) {
					showJobNotification(this.props.intl, response.retryIncomingEmails.job);

					const jobRefId = this._getJobRefId(response.retryIncomingEmails.job);

					this.setState({
						lastBatchJobId: jobRefId,
					});

					this._setStoredBatchJobId(jobRefId);
					if (this.props.relay && this.props.relay.setVariables) {
						this.props.relay.setVariables({ batchJobId: jobRefId });
					}
				}

				resolve();
			};

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
					organisation_id: user.organisation.realId,
					flow_id: user.organisation.flow.realId,
					sinceDate: this.state.batchSinceDate || undefined,
					toDate: this.state.batchToDate || undefined,
					failed: this.state.batchFailed ? true : undefined,
					unprocessed: this.state.batchUnprocessed ? true : undefined,
					stopOnFailure: this.state.batchStopOnFailure,
				}),
				{ onSuccess: onSuccess, onFailure: reject },
			);
		});
	}

	onClickStopBatchJob() {
		return new Promise((resolve, reject) => {
			let flow = this.props.user.organisation.flow;
			let flowRealId = flow.realId;
			let flowId = flow.id;
			let selectedBatchJob = ((this.props.user || {}).organisation || {}).job;
			let jobs = this._getAllKnownJobsFromProps();
			let runningBatchJobNode = jobs.find((item) => {
				if (!item || item.type !== "retry-incoming-mails") {
					return false;
				}

				if (!this._isRunningStatus(item.status)) {
					return false;
				}

				return this._jobFlowMatches(item, flowRealId, flowId);
			});

			let activeBatchJobRefId = this.state.lastBatchJobId || this._getStoredBatchJobId() || this._getJobRefId(runningBatchJobNode);
			let selectedRunningBatchJob = selectedBatchJob && selectedBatchJob.type === "retry-incoming-mails" && this._isRunningStatus(selectedBatchJob.status) && this._jobFlowMatches(selectedBatchJob, flowRealId, flowId) ? selectedBatchJob : null;

			let knownJobs = [selectedRunningBatchJob, runningBatchJobNode].concat(jobs.filter(Boolean));
			let matchedJob = knownJobs.find((job) => this._getJobRefId(job) === activeBatchJobRefId);
			let activeBatchJobId = (matchedJob && matchedJob.id) || (selectedRunningBatchJob && selectedRunningBatchJob.id) || activeBatchJobRefId;

			if (!activeBatchJobId) {
				resolve();
				return;
			}

			let confirmDialogConfig = {
				message: this.props.intl.formatMessage(messages.stopBatchDescription),
				confirm: this.props.intl.formatMessage(messages.stopBatchButton),
				confirmStyle: "btn-danger",
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig)
				.then(() => {
					let onSuccess = () => {
						this.setState({
							lastBatchJobId: null,
						});

						this._setStoredBatchJobId("");
						if (this.props.relay && this.props.relay.setVariables) {
							this.props.relay.setVariables({ batchJobId: "" });
						}
						resolve();
					};

					this.props.relay.commitUpdate(
						new CancelJobMutation({
							job_id: activeBatchJobId,
						}),
						{ onSuccess, onFailure: reject },
					);
				})
				.catch(() => {
					resolve();
				});
		});
	}

	retryFailedIncomingEmails() {
		let { user } = this.props;

		return new Promise((resolve, reject) => {
			let onSuccess = (response) => {
				if (response.retryIncomingEmails.job) {
					showJobNotification(this.props.intl, response.retryIncomingEmails.job);
				}

				resolve();
			};

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
					organisation_id: user.organisation.realId,
					flow_id: user.organisation.flow.realId,
					failed: true,
				}),
				{ onSuccess: onSuccess, onFailure: reject },
			);
		});
	}

	onClickRetryUnprocessedIncomingEmails() {
		return new Promise((resolve) => {
			let confirmDialogConfig = {
				message: this.props.intl.formatMessage(messages.retryUnprocessedIncomingEmailsDescription),
				confirm: this.props.intl.formatMessage(messages.retryUnprocessedIncomingEmailsButton),
				confirmStyle: "btn-danger",
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig)
				.then(() => {
					return this.retryUnprocessedIncomingEmails().then(() => {
						return resolve();
					});
				})
				.catch(() => {
					resolve();
				});
		});
	}

	retryUnprocessedIncomingEmails() {
		let { user } = this.props;

		return new Promise((resolve, reject) => {
			let onSuccess = (response) => {
				if (response.retryIncomingEmails.job) {
					showJobNotification(this.props.intl, response.retryIncomingEmails.job);
				}

				resolve();
			};

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
					organisation_id: user.organisation.realId,
					flow_id: user.organisation.flow.realId,
					processed: false,
				}),
				{ onSuccess: onSuccess, onFailure: reject },
			);
		});
	}

	render() {
		let flow = this.props.user.organisation.flow;
		let flowRealId = flow.realId;
		let flowId = flow.id;
		const { flowIncomingEmails } = flow;
		const selectedBatchJob = this.props.user.organisation.job;
		const knownJobs = this._getAllKnownJobsFromProps();
		const runningBatchJobFromList = knownJobs.find((item) => {
			if (!item || item.type !== "retry-incoming-mails") {
				return false;
			}

			if (!this._isRunningStatus(item.status)) {
				return false;
			}

			return this._jobFlowMatches(item, flowRealId, flowId);
		});
		const runningBatchJobFromSelected = selectedBatchJob && selectedBatchJob.type === "retry-incoming-mails" && this._isRunningStatus(selectedBatchJob.status) ? selectedBatchJob : null;

		const runningBatchJob = runningBatchJobFromSelected || runningBatchJobFromList;
		const knownBatchJobId = this.state.lastBatchJobId || this._getStoredBatchJobId() || this._getJobRefId(runningBatchJob);
		const fallbackBatchJob = this._findBatchJobByRefFromProps(knownBatchJobId);
		const displayBatchJob = runningBatchJob || fallbackBatchJob;
		const hasRunningDisplayJob = !!(displayBatchJob && this._isRunningStatus(displayBatchJob.status));
		const shouldShowBatchStatus = hasRunningDisplayJob || (!!knownBatchJobId && !displayBatchJob);

		const runningBatchJobProgress = this._parseProgress(displayBatchJob && displayBatchJob.progress);
		const hasValidProgress = runningBatchJobProgress && isFinite(Number(runningBatchJobProgress.currentProgress)) && isFinite(Number(runningBatchJobProgress.maxProgress));
		const runningProgressCurrent = hasValidProgress ? Number(runningBatchJobProgress.currentProgress) : null;
		const runningProgressMax = hasValidProgress ? Number(runningBatchJobProgress.maxProgress) : null;
		const runningProgressRemaining = hasValidProgress ? (runningProgressMax > runningProgressCurrent ? runningProgressMax - runningProgressCurrent : 0) : null;

		const runningBatchJobTasks = (displayBatchJob && displayBatchJob.jobTasks) || [];
		const hasBatchJobDetails = !!displayBatchJob;
		const runningTask = runningBatchJobTasks.find((task) => task && task.status === "in-progress") || runningBatchJobTasks.find((task) => task && task.status === "waiting") || null;
		const runningTaskProgress = this._parseProgress(runningTask && runningTask.progress);
		const phaseStatus = (runningTask && runningTask.status) || (displayBatchJob && displayBatchJob.status) || "waiting";
		let runningTaskPhase = this.props.intl.formatMessage(messages.batchPhaseWaitingLabel);
		if (phaseStatus === "in-progress" || phaseStatus === "running") {
			runningTaskPhase = this.props.intl.formatMessage(messages.batchPhaseProcessingLabel);
		} else if (phaseStatus === "finished" || phaseStatus === "failed" || phaseStatus === "skipped") {
			runningTaskPhase = this.props.intl.formatMessage(messages.batchPhaseDoneLabel);
		}
		const runningTaskCurrent = runningTaskProgress ? Number(runningTaskProgress.currentProgress || 0) : null;
		const runningTaskMax = runningTaskProgress ? Number(runningTaskProgress.maxProgress || 0) : null;
		const batchValidationMessage = this._getBatchValidationError();

		const list = flowIncomingEmails;
		let type = types[this.typeName];

		return (
			<AppPanel
				breadcrumbParams={[
					{ type: types["organisations"], data: this.props.user.organisation },
					{ type: types["flows"], data: flow },
				]}
				title={this.props.intl.formatMessage(messages.title)}
			>
				<div>
					<div aria-label="..." className="btn-group" role="group" style={{ paddingRight: "1em" }}>
						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, "list", { ...flow })}>
							<FormattedMessage defaultMessage="All" id="flowIncomingEmailList.listAll" />
						</Link>
						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, "list-unprocessed", { ...flow })}>
							<FormattedMessage defaultMessage="Unprocessed" id="flowIncomingEmailList.listUnprocessed" />
						</Link>
						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, "list-failed", { ...flow })}>
							<FormattedMessage defaultMessage="Failed" id="flowIncomingEmailList.listFailed" />
						</Link>
					</div>

					<div aria-label="..." className="btn-group" role="group" style={{ paddingRight: "1em" }}>
						<LoadingButton
							className="btn btn-default"
							disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked}
							icon="fa-refresh"
							key="failed-mails"
							onClick={() => {
								this.onClickRetryFailedIncomingEmails();
							}}
						>
							<i className="fa fa-fw fa-envelope"></i> <FormattedMessage defaultMessage="Retry failed e-mails" id="invokeAllButton.retryFailedIncomingEmails" />
						</LoadingButton>
						<LoadingButton
							className="btn btn-default"
							disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked}
							icon="fa-refresh"
							key="unprocessed-mails"
							onClick={() => {
								this.onClickRetryUnprocessedIncomingEmails();
							}}
						>
							<i className="fa fa-fw fa-envelope"></i> <FormattedMessage defaultMessage="Retry unprocessed e-mails" id="invokeAllButton.retryUnprocessedIncomingEmails" />
						</LoadingButton>
					</div>

					{BrowserPermission.isDeveloper(this.props.user.organisation.realId) && (
						<div style={{ marginBottom: "1em", marginTop: "1em" }}>
							<LoadingButton className="btn btn-default" icon="fa-filter" key="toggle-batch-mails" onClick={() => this.setState({ showBatchSection: !this.state.showBatchSection })}>
								<i className="fa fa-fw fa-calendar"></i> <FormattedMessage {...messages.toggleBatchSectionButton} />
							</LoadingButton>

							{this.state.showBatchSection && (
								<div className="well" style={{ marginTop: "0.75em", marginBottom: 0 }}>
									<div className="alert alert-info" style={{ marginBottom: "0.75em" }}>
										<strong>
											<FormattedMessage {...messages.batchHintTitle} />
										</strong>
										<div>
											<FormattedMessage {...messages.batchHintSequential} />
										</div>
										<div>
											<FormattedMessage {...messages.batchHintContinue} />
										</div>
										<div>
											<FormattedMessage {...messages.batchHintStop} />
										</div>
									</div>
									<div className="row" style={{ marginBottom: "0.5em" }}>
										<div className="col-md-3">
											<label>
												<FormattedMessage {...messages.fromDateLabel} />
											</label>
											<input className="form-control" onChange={(e) => this.setState({ batchSinceDate: e.target.value })} type="date" value={this.state.batchSinceDate} />
										</div>
										<div className="col-md-3">
											<label>
												<FormattedMessage {...messages.toDateLabel} />
											</label>
											<input className="form-control" onChange={(e) => this.setState({ batchToDate: e.target.value })} type="date" value={this.state.batchToDate} />
										</div>
										<div className="col-md-3" style={{ paddingTop: "1.8em" }}>
											<label>
												<input checked={this.state.batchFailed} onChange={(e) => this.setState({ batchFailed: e.target.checked })} type="checkbox" /> <FormattedMessage {...messages.batchFailedLabel} />
											</label>
											<br />
											<label>
												<input checked={this.state.batchUnprocessed} onChange={(e) => this.setState({ batchUnprocessed: e.target.checked })} type="checkbox" /> <FormattedMessage {...messages.batchUnprocessedLabel} />
											</label>
										</div>
										<div className="col-md-3" style={{ paddingTop: "1.8em" }}>
											<label>
												<input checked={this.state.batchStopOnFailure} name="batchFailureMode" onChange={() => this.setState({ batchStopOnFailure: true })} type="radio" /> <FormattedMessage {...messages.stopOnFailureLabel} />
											</label>
											<br />
											<label>
												<input checked={!this.state.batchStopOnFailure} name="batchFailureMode" onChange={() => this.setState({ batchStopOnFailure: false })} type="radio" /> <FormattedMessage {...messages.continueOnFailureLabel} />
											</label>
										</div>
									</div>
									<LoadingButton
										className="btn btn-primary"
										disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked || shouldShowBatchStatus || !!batchValidationMessage}
										icon="fa-refresh"
										key="batch-mails"
										onClick={() => {
											this.onClickProcessBatchIncomingEmails();
										}}
									>
										<i className="fa fa-fw fa-envelope"></i> <FormattedMessage {...messages.processBatchButton} />
									</LoadingButton>{" "}
									{shouldShowBatchStatus && (
										<LoadingButton
											className="btn btn-danger"
											icon="fa-stop"
											key="stop-batch-mails"
											onClick={() => {
												this.onClickStopBatchJob();
											}}
										>
											<i className="fa fa-fw fa-stop"></i> <FormattedMessage {...messages.stopBatchButton} />
										</LoadingButton>
									)}
									{shouldShowBatchStatus && (
										<div style={{ marginLeft: "0.75em", display: "inline-block", verticalAlign: "middle" }}>
											<FormattedMessage {...messages.batchRunningLabel} />
											{hasValidProgress && (
												<span>
													{" "}
													• <FormattedMessage {...messages.batchProgressLabel} values={{ current: runningProgressCurrent, max: runningProgressMax }} />
												</span>
											)}
											{hasBatchJobDetails && hasValidProgress ? (
												<div>
													<FormattedMessage {...messages.batchTotalLabel} values={{ count: runningProgressMax }} />
													{" • "}
													<FormattedMessage {...messages.batchProcessedLabel} values={{ count: runningProgressCurrent }} />
													{" • "}
													<FormattedMessage {...messages.batchRemainingLabel} values={{ count: runningProgressRemaining }} />
												</div>
											) : (
												<div>
													<FormattedMessage {...messages.batchProgressLoadingLabel} />
												</div>
											)}
											{hasBatchJobDetails && (
												<div>
													<FormattedMessage
														{...messages.batchCurrentTaskLabel}
														values={{
															phase: runningTaskCurrent !== null && runningTaskMax !== null ? runningTaskPhase + " (" + runningTaskCurrent + "/" + runningTaskMax + ")" : runningTaskPhase,
														}}
													/>
												</div>
											)}
										</div>
									)}
									{batchValidationMessage && (
										<div className="text-danger" style={{ marginTop: "0.5em" }}>
											<FormattedMessage {...batchValidationMessage} />
										</div>
									)}
								</div>
							)}
						</div>
					)}

					<PagingGrid columns={["emailSender", "emailRecipeint", "subject", "createdAt", "processed", "failed", "nextRetry"]} edges={list} metadata={type} pageInfo={list.pageInfo} relay={this.props.relay} urlMapping={urlMapping} />
				</div>
			</AppPanel>
		);
	}
}

export default Relay.createContainer(
	wrapNotFound(injectIntl(FlowIncomingEmailList), (props) => {
		return props.user.organisation && props.user.organisation.flow;
	}),
	{
		initialVariables: {
			organisationId: "",
			flowId: "",
			batchJobId: "",
			first: defaultPageSize,
			after: null,
			before: null,
			last: null,
			failed: null,
			processed: null,
			type: null,
		},
		prepareVariables(variables) {
			if (variables.type) {
				if (variables.type === "failed") {
					variables.failed = true;
				} else if (variables.type === "unprocessed") {
					variables.processed = false;
				}
			} else {
				variables.failed = null;
				variables.processed = null;
			}

			if (!variables.batchJobId) {
				const mapRaw = StorageAdapter.getSync("flowIncomingEmails:batchJobByFlow") || "{}";
				const mapParsed = safeJSONparse(mapRaw) || {};
				variables.batchJobId = mapParsed[variables.flowId] || "";
			}

			processPagingParams(variables);

			return variables;
		},
		fragments: {
			user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    blocked,
					jobs(first: 300, orderBy: "createdAt desc") {
                        edges {
                            node {
                                id,
                                realId,
                                type,
                                status,
                                progress,
                                args,
								jobTasks {
									id,
									type,
									typeName,
									name,
									status,
									stage,
									progress,
								}
                            }
                        }
                    }
                    job(id: $batchJobId) {
                        id,
                        realId,
                        type,
                        status,
                        progress,
                        args,
						jobTasks {
							id,
							type,
							typeName,
							name,
							status,
							stage,
							progress,
						}
                    }
                    flow(id: $flowId) {
                        id,
                        realId,
                        organisation_id,
                        name,
                        paused,
                        enabled,
                        flowDef {
                            id,
                            realId,
                            globallyDisabled,
                        },
                        flowIncomingEmails(first: $first, after: $after, before: $before, last: $last, failed: $failed, processed: $processed) {
                            edges {
                                node {
                                    id,
                                    realId,
                                    flow_id,
                                    nextRetry,
                                    organisation_id,
                                    emailSender,
                                    emailRecepient,
                                    emailSubject,
                                    processed,
                                    failed,
                                    createdAt,
                                }
                            },
                            pageInfo {
                                hasNextPage,
                                hasPreviousPage,
                                startCursor,
                                endCursor
                            }
                        }
                    }
                }
				jobs(first: 300, orderBy: "createdAt desc") {
					edges {
						node {
							id,
							realId,
							type,
							status,
							progress,
							args,
							jobTasks {
								id,
								type,
								typeName,
								name,
								status,
								stage,
								progress,
							}
						}
					}
				}
            }
        `,
		},
	},
);
